import { z } from 'zod';
import { agentProtocolSchema, AgentTypeEnum, agentTypeSchema } from '../enums/agent.js';
import { ComplianceConfigSchema } from './compliance.js';
import { StatusTypeSchema } from '../enums/status.js';
import {
  agentProtocolDataSchema,
  agentProtocolDetailsDataSchema,
  internalProtocolSchema,
} from './agent.protocol.js';

export const agentSkillSchema = z.object({
  identifier: z.string(),
  name: z.string(),
  description: z.string(),
  examples: z.array(z.string()),
  tags: z.array(z.string()),
});
export type AgentSkill = z.infer<typeof agentSkillSchema>;

export const customAgentDataSchema = z.object({});


export const csvAgentDetailsDataSchema = z.object({
  prompt: z.string().optional(),
});
export const orchestrationAgentDetailsDataSchema = z.object({});

export const subAgentTableItemSchema = z.object({
  id: z.coerce.number(),
  identifier: z.string(),
  name: z.string(),
  description: z.string(),
  protocol: agentProtocolSchema.nullable(),
  typeId: agentTypeSchema,
});
export type SubAgentTableItem = z.infer<typeof subAgentTableItemSchema>;

const agentFileTableItemSchema = z.object({
  id: z.coerce.number(),
  identifier: z.string(),
  originalName: z.string(),
});

const baseAgentSchema = z.object({
  id: z.coerce.number(),
  identifier: z.string(),
  description: z.string(),
  inputSchema: z.record(z.string(), z.unknown()),
  name: z.string(),
  status: StatusTypeSchema,
  createdOn: z.coerce.date(),
  skills: z.array(agentSkillSchema).default([]),
  files: z.array(agentFileTableItemSchema).default([]),
  systemPrompt: z.string().nullable(),
  complianceConfig: ComplianceConfigSchema.optional().nullable(),
  tags: z.array(z.string()).default([]),
});

export const customAgentSchema = baseAgentSchema.merge(z.object({
  data: customAgentDataSchema,
  typeId: z.literal(AgentTypeEnum.Default),
  protocol: agentProtocolDataSchema,
}));
export const customAgentDetailsSchema = baseAgentSchema.merge(z.object({
  data: customAgentDataSchema,
  typeId: z.literal(AgentTypeEnum.Default),
  protocol: agentProtocolDetailsDataSchema,
}));

export const csvAgentSchema = baseAgentSchema.merge(z.object({
  data: csvAgentDetailsDataSchema,
  typeId: z.literal(AgentTypeEnum.CSVAgent),
  protocol: agentProtocolDataSchema,
}));
export const csvAgentDetailSchema = baseAgentSchema.merge(z.object({
  data: csvAgentDetailsDataSchema,
  typeId: z.literal(AgentTypeEnum.CSVAgent),
  protocol: agentProtocolDetailsDataSchema,
}));

export const orchestrationAgentSchema = baseAgentSchema.merge(z.object({
  data: orchestrationAgentDetailsDataSchema,
  typeId: z.literal(AgentTypeEnum.Orchestration),
  protocol: agentProtocolDataSchema,
}));
export const orchestrationAgentDetailSchema = baseAgentSchema.merge(z.object({
  data: orchestrationAgentDetailsDataSchema,
  typeId: z.literal(AgentTypeEnum.Orchestration),
  protocol: agentProtocolDetailsDataSchema,
}));


export const agentSchema = z.discriminatedUnion('typeId', [
  customAgentSchema,
  csvAgentSchema,
  orchestrationAgentSchema,
]);

export const agentDetailsSchema = z.discriminatedUnion('typeId', [
  customAgentDetailsSchema,
  csvAgentDetailSchema,
  orchestrationAgentDetailSchema,
]);

export type Agent = z.infer<typeof agentSchema>;
export type CustomAgent = z.infer<typeof customAgentSchema>;
export type CsvAgent = z.infer<typeof csvAgentSchema>;
export type OrchestrationAgent = z.infer<typeof orchestrationAgentSchema>;

export const formWorkflowAgentConfigSchema = z.object({
  llmModel: z.enum(['gpt-35-turbo', 'gpt-4o', 'gpt-4o-mini', 'gpt-4.1-mini', 'gpt-4.1']),
  temperature: z.number(),
  enableAutoExtraction: z.boolean(),
  enableInteractiveMode: z.boolean(),
  autoSelectOnSingleMatch: z.boolean(),
  maxCandidatesToShow: z.number(),
  contentLengthThreshold: z.number(),
  customPrompts: z.object({
    systemPrompt: z.string().optional(),
    matchingPrompt: z.string().optional(),
    extractionPrompt: z.string().optional(),
  }).optional(),
});

export const formWorkflowContextSchema = z.object({
  brandIdentifier: z.string(),
  localUserId: z.number(),
  physicianName: z.string().nullable(),
});

export const contextSpecificConfigSchema = z.object({
  global: formWorkflowAgentConfigSchema.partial(),
  kol: formWorkflowAgentConfigSchema.partial(),
  standalone: formWorkflowAgentConfigSchema.partial(),
});


export type AgentDetails = z.infer<typeof agentDetailsSchema>;
export type CustomAgentDetails = z.infer<typeof customAgentDetailsSchema>;
export type CsvAgentDetails = z.infer<typeof csvAgentDetailSchema>;
export type OrchestrationAgentDetails = z.infer<typeof orchestrationAgentDetailSchema>;

export type FormWorkflowAgentConfig = z.infer<typeof formWorkflowAgentConfigSchema>;
export type FormWorkflowContext = z.infer<typeof formWorkflowContextSchema>;
export type ContextSpecificConfig = z.infer<typeof contextSpecificConfigSchema>;